import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getSelfRetailerId } from "@/components/feature/auth/session";
import { kycService, retailerService, walletService } from "@/services/apiServices";
import { setWalletRates } from "@/pages/retailer/wallet/constants";
import type { RetailerProfile, WalletBalance } from "@/types/wallet";

interface RetailerSessionValue {
  retailerId: string;
  profile: RetailerProfile | null;
  wallet: WalletBalance | null;
  kycStatus: string;
  kycRejectionReason: string | null;
  loading: boolean;
  isKycApproved: boolean;
  canCreateMandate: boolean;
  refresh: () => Promise<void>;
  refreshWallet: () => Promise<void>;
}

const RetailerSessionContext = createContext<RetailerSessionValue | null>(null);

export function RetailerSessionProvider({ children }: { children: ReactNode }) {
  const retailerId = getSelfRetailerId();
  const [profile, setProfile] = useState<RetailerProfile | null>(null);
  const [wallet, setWallet] = useState<WalletBalance | null>(null);
  const [kycStatus, setKycStatus] = useState<string>("NOT_STARTED");
  const [kycRejectionReason, setKycRejectionReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshWallet = useCallback(async () => {
    try {
      const res = await walletService.getBalance(retailerId);
      if (res?.data) {
        setWallet(res.data);
        if (res.data.creationRate) {
          setWalletRates(
            res.data.creationRate,
            res.data.installmentRate ?? 40,
            res.data.lowBalanceThreshold ?? 500
          );
        }
      }
    } catch {
      /* keep the last known balance if the call fails */
    }
  }, [retailerId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [profileRes, walletRes, kycRes] = await Promise.allSettled([
      retailerService.getProfile(retailerId),
      walletService.getBalance(retailerId),
      kycService.getStatus(retailerId),
    ]);

    if (profileRes.status === "fulfilled" && profileRes.value?.data?.profile) {
      setProfile(profileRes.value.data.profile);
    }
    if (walletRes.status === "fulfilled" && walletRes.value?.data) {
      const w = walletRes.value.data;
      setWallet(w);
      if (w.creationRate) {
        setWalletRates(
          w.creationRate,
          w.installmentRate ?? 40,
          w.lowBalanceThreshold ?? 500
        );
      }
    }
    if (kycRes.status === "fulfilled" && kycRes.value?.data) {
      setKycStatus(kycRes.value.data.status || "NOT_STARTED");
      setKycRejectionReason(kycRes.value.data.record?.rejectionReason ?? null);
    }
    setLoading(false);
  }, [retailerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<RetailerSessionValue>(() => {
    const approved = kycStatus === "APPROVED";
    return {
      retailerId,
      profile,
      wallet,
      kycStatus,
      kycRejectionReason,
      loading,
      isKycApproved: approved,
      canCreateMandate: approved && (wallet?.availableBalance ?? 0) >= (wallet?.creationRate ?? 500),
      refresh,
      refreshWallet,
    };
  }, [retailerId, profile, wallet, kycStatus, kycRejectionReason, loading, refresh, refreshWallet]);

  return <RetailerSessionContext.Provider value={value}>{children}</RetailerSessionContext.Provider>;
}

/**
 * Live retailer session (profile, wallet and KYC status) shared across the retailer app.
 * Falls back to a neutral, non-blocking state when used outside the provider.
 */
export function useRetailerSession(): RetailerSessionValue {
  const ctx = useContext(RetailerSessionContext);
  if (ctx) return ctx;
  return {
    retailerId: getSelfRetailerId(),
    profile: null,
    wallet: null,
    kycStatus: "NOT_STARTED",
    kycRejectionReason: null,
    loading: true,
    isKycApproved: false,
    canCreateMandate: false,
    refresh: async () => {},
    refreshWallet: async () => {},
  };
}
